import { Star } from 'lucide-react'
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'arcade'
/** Arcade buttons: green "go" to start, blue "join" to join, violet "boss" to challenge the computer. */
export type ArcadeTone = 'go' | 'join' | 'boss' | 'drop'

const variants: Record<Variant, string> = {
  primary:
    'bg-slate-900 text-white hover:bg-slate-700 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200',
  secondary:
    'bg-white text-slate-900 ring-1 ring-slate-300 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700 dark:hover:bg-slate-800',
  ghost: 'text-slate-700 hover:bg-slate-200/70 dark:text-slate-200 dark:hover:bg-slate-800',
  danger: 'bg-rose-600 text-white hover:bg-rose-500',
  arcade: 'text-white hover:brightness-110 enabled:animate-arcade-pulse',
}

const ARCADE_TONES: Record<ArcadeTone, string> = {
  go: 'bg-[linear-gradient(180deg,var(--arcade-go-1),var(--arcade-go-2))]',
  join: 'bg-[linear-gradient(180deg,var(--arcade-join-1),var(--arcade-join-2))]',
  boss: 'bg-[linear-gradient(180deg,var(--arcade-boss-1),var(--arcade-boss-2))]',
  drop: 'bg-[linear-gradient(180deg,var(--arcade-drop-1),var(--arcade-drop-2))]',
}

/** Chunky pressable slab: a 5px bottom edge that collapses on press. Disabled = flat and grey. */
const arcadeBase = [
  'inline-flex min-h-14 items-center justify-center gap-2 rounded-2xl px-7 py-3 text-lg font-black uppercase tracking-wide',
  'shadow-[0_5px_0_var(--arcade-edge),0_10px_20px_-8px_rgba(0,0,0,.5)] transition-[transform,box-shadow,filter] duration-100',
  'active:translate-y-[3px] active:shadow-[0_2px_0_var(--arcade-edge)] active:animate-none',
  'disabled:cursor-not-allowed disabled:bg-none disabled:bg-slate-400 disabled:shadow-none disabled:opacity-70 dark:disabled:bg-slate-600',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500',
].join(' ')

const buttonBase = [
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-colors',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500',
].join(' ')

/** The arcade slab's full class list; `Button variant="arcade"` and presentational slabs share it. */
export function arcadeClass(tone: ArcadeTone): string {
  return `${arcadeBase} ${ARCADE_TONES[tone]} ${variants.arcade}`
}

export function Button({
  variant = 'primary',
  tone = 'go',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; tone?: ArcadeTone }) {
  const base =
    variant === 'arcade'
      ? arcadeClass(tone)
      : `${buttonBase} disabled:cursor-not-allowed disabled:opacity-50 ${variants[variant]}`
  return <button type="button" {...props} className={`${base} ${className}`} />
}

const iconButtonBase = [
  'inline-flex size-10 shrink-0 items-center justify-center rounded-full text-slate-600 transition-colors',
  'hover:bg-slate-200/70 disabled:opacity-30 disabled:hover:bg-transparent dark:text-slate-300 dark:hover:bg-slate-800',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500',
].join(' ')

/** Round icon-only button; `label` is required because there is no visible text. */
export function IconButton({
  label,
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      {...props}
      className={`${iconButtonBase} ${className}`}
    />
  )
}

/** Router link styled like IconButton (back arrows and the like). */
export function IconLink({ label, className = '', ...props }: LinkProps & { label: string }) {
  return (
    <Link
      aria-label={label}
      title={label}
      {...props}
      className={`${iconButtonBase} ${className}`}
    />
  )
}

/** A router link that looks like a Button (use for navigation, never for actions). */
export function LinkButton({
  variant = 'ghost',
  className = '',
  ...props
}: LinkProps & { variant?: Variant }) {
  return <Link {...props} className={`${buttonBase} ${variants[variant]} ${className}`} />
}

interface SegmentedProps<T extends string> {
  value: T
  onChange: (next: T) => void
  options: readonly { value: T; label: string }[]
  /** Accessible name of the group. */
  label: string
  /** `dark` sits on a dark card (the boss fight). */
  tone?: 'light' | 'dark'
  className?: string
}

/** Pill radio group: one option is on at a time. */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
  tone = 'light',
  className = '',
}: SegmentedProps<T>) {
  const track =
    tone === 'dark'
      ? 'bg-white/10 ring-1 ring-white/15'
      : 'bg-slate-200/80 ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700'
  const on =
    tone === 'dark'
      ? 'bg-white text-slate-900 shadow'
      : 'bg-white text-slate-900 shadow dark:bg-slate-100'
  const off =
    tone === 'dark'
      ? 'text-white/80 hover:text-white'
      : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white'
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={`flex rounded-full p-1 ${track} ${className}`}
    >
      {options.map((o) => {
        const active = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(o.value)}
            className={`flex-1 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500 ${active ? on : off}`}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

/** Accessible on/off toggle. Label it with `aria-labelledby` or `aria-label`. */
export function Switch({
  checked,
  onChange,
  className = '',
  ...props
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> & {
  checked: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      {...props}
      className={[
        'relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500',
        checked ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-700',
        className,
      ].join(' ')}
    >
      <span
        aria-hidden
        className={[
          'inline-block size-5 rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-6' : 'translate-x-1',
        ].join(' ')}
      />
    </button>
  )
}

export function Input({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={[
        'min-h-11 w-full rounded-xl bg-white px-3.5 text-base text-slate-900 ring-1 ring-slate-300 placeholder:text-slate-400',
        'focus:outline-2 focus:outline-offset-2 focus:outline-sky-500',
        'dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700',
        className,
      ].join(' ')}
    />
  )
}

export function Card({ className = '', children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={[
        'rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  )
}

/** Fills the main column and centers a single card/message (loading, errors, closed rooms). */
export function Centered({ children }: { children: ReactNode }) {
  return <div className="flex flex-1 items-center justify-center py-10">{children}</div>
}

/** Three-star rating; `earned` 0 renders all stars empty. */
export function Stars({ earned, size = 'sm' }: { earned: 0 | 1 | 2 | 3; size?: 'sm' | 'lg' }) {
  const cls = size === 'lg' ? 'size-8' : 'size-3'
  return (
    <span
      role="img"
      className={`flex gap-0.5 ${size === 'lg' ? 'justify-center gap-1' : ''}`}
      aria-label={earned ? `${earned} of 3 stars` : 'not cleared'}
    >
      {[1, 2, 3].map((i) => (
        <Star
          key={i}
          className={`${cls} ${i <= earned ? 'fill-amber-400 text-amber-400' : 'text-slate-300 dark:text-slate-600'}`}
          aria-hidden
        />
      ))}
    </span>
  )
}
